import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { Grid, Menu, Divider, Segment, Button, Card, Tab, Loader, List } from "semantic-ui-react";

import { Detail } from "@neworbit/simpleui-detail";

import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { getIdentityValidationOptionsValue } from "@teamprevent/common/immunisation/models";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { immunisationSelector, basePathSelector, routeIdSelector } from "../selectors";
import { Immunisation } from "../model";
import { deleteImmunisation, loadImmunisationDetail } from "../actions";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { ApplicationState } from "../../applicationState";

import { Link } from "redux-little-router";

interface StateProps {
    immunisation: Immunisation | undefined;
    path: string | undefined;
    customerId: string;
    basePath: string;
    employeeRecordId: string;
    immunisationId: string;
}

interface DispatchProps {
    delete: (employeeRecordId: string, id: string, basePath: string) => void;
    get: (employeeRecordId: string, id: string) => void;
}

class DetailsUnconnected extends React.Component<StateProps & DispatchProps> {
    private showClinicalNotesOnImmunisationRecord = false;

    constructor(props: StateProps & DispatchProps) {
        super(props);

        props.get(props.employeeRecordId, props.immunisationId);
        this.showClinicalNotesOnImmunisationRecord = getFeatureToggles()[FeatureToggleNames.ShowClinicalNotesOnImmunisationRecord];

        this.onClickDelete = this.onClickDelete.bind(this);
    }

    private onClickDelete = () => {
        this.props.delete(this.props.employeeRecordId, this.props.immunisationId, this.props.basePath);
    };

    public render() {
        if (!this.props.immunisation) {
            return <Loader active />;
        }

        const ohCase = this.props.immunisation.ohCase;

        return (
            <Grid container className="nomargintop">
                <Grid.Row>
                    <Grid.Column>
                        <h1>{this.props.immunisation.immunisationType.name}</h1>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={3} as={Grid} padded>
                        <Grid.Column vertical as={Menu}>
                            <Menu.Item name="AdminUsers" as={Link} href={this.props.basePath}>
                                All Employee Immunisations
                            </Menu.Item>
                        </Grid.Column>
                    </Grid.Column>
                    <Grid.Column width={13}>
                        <Card fluid>
                            <Tab.Pane>
                                <Grid>
                                    <Detail label="Immunisation" value={this.props.immunisation.immunisationType.name} labelWidth={5} valueWidth={11} />
                                    <Detail
                                        label="Date complete"
                                        value={this.props.immunisation.dateCompleted && this.props.immunisation.dateCompleted.format("DD/MM/YYYY")}
                                        labelWidth={5}
                                        valueWidth={11}
                                    />
                                    <Detail label="Type" value={this.props.immunisation.recordType} labelWidth={5} valueWidth={11} />
                                    <Detail label="Outcome" value={this.props.immunisation.outcome} labelWidth={5} valueWidth={11} />
                                    <Detail
                                        label="IVS Sample"
                                        value={getIdentityValidationOptionsValue(this.props.immunisation.identityValidation)}
                                        labelWidth={5}
                                        valueWidth={11}
                                    />
                                    <Detail label="Notes" value={this.props.immunisation.notes} labelWidth={5} valueWidth={11} />
                                    {ohCase ?
                                        <Grid.Row>
                                            <Grid.Column width={5} as="h4">
                                                <Link
                                                    // eslint-disable-next-line max-len
                                                    href={`/customers/${this.props.customerId}/employeeRecords/${this.props.immunisation.employeeRecordId}/ohCases/${ohCase.id}`}
                                                >
                                                    Case ID
                                                </Link>
                                            </Grid.Column>
                                            <Grid.Column width={11}>
                                                <Link
                                                    // eslint-disable-next-line max-len
                                                    href={`/customers/${this.props.customerId}/employeeRecords/${this.props.immunisation.employeeRecordId}/ohCases/${ohCase.id}`}
                                                >
                                                    {ohCase.name}
                                                </Link>
                                            </Grid.Column>
                                        </Grid.Row> :
                                        <Detail label="Case ID" value="No associated case" labelWidth={5} valueWidth={11} />
                                    }
                                    <Detail
                                        label="Evidence"
                                        value={this.props.immunisation.evidence ?
                                            <List>
                                                {this.props.immunisation.evidence.length ?
                                                    this.props.immunisation.evidence.map(e =>
                                                        (<List.Item key={e.blobId}>
                                                            <a
                                                                // eslint-disable-next-line max-len
                                                                href={`/api/employeeRecord/${this.props.immunisation?.employeeRecordId}/immunisations/evidence/${e.blobId}?fileName=${e.fileName}`}
                                                            >
                                                                {e.fileName}
                                                            </a>
                                                        </List.Item>)) :
                                                    <span>No attached evidence</span>
                                                }
                                            </List> :
                                            <Loader active />}
                                        labelWidth={5}
                                        valueWidth={11}
                                    />
                                    {this.props.immunisation.details && this.showClinicalNotesOnImmunisationRecord &&
                                        <Detail label="Clinical Notes / Measurements" value={this.props.immunisation.details} labelWidth={5} valueWidth={11} />}
                                </Grid>
                            </Tab.Pane>
                        </Card>
                        <Divider />
                        <Segment basic clearing vertical>
                            <ConfirmedButton
                                icon="delete"
                                content="Delete"
                                floated="left"
                                className="warning"
                                onClick={this.onClickDelete}
                                promptText="Are you sure you want to delete this immunisation record? Type 'DELETE' and click confirm to delete."
                                typeToConfirmText="DELETE"
                                headingText="Delete Immunisation"
                            />
                            <Button icon="pencil" content="Edit" floated="right" as={Link} href={`${this.props.path}/edit`} />
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    }
}

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        immunisation: immunisationSelector(state),
        customerId: customerIdSelector(state),
        path: state.router.pathname,
        basePath: basePathSelector(state),
        employeeRecordId: state.router.params?.employeeRecordId || "",
        immunisationId: routeIdSelector(state)
    };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
    return {
        delete: (employeeRecordId: string, id: string, basePath: string) => dispatch<any>(deleteImmunisation(employeeRecordId, id, basePath)),
        get: (employeeRecordId: string, id: string) => dispatch<any>(loadImmunisationDetail({ employeeRecordId, immunisationId: id }))
    };
}

export const Details = connect(mapStateToProps, mapDispatchToProps)(DetailsUnconnected);
